import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Check } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, AdminField } from "./shared";

const SECTION_LABEL_KEY = {
    overview: "admin.overview",
    analytics: "admin.analytics",
    bookings: "admin.reservations",
    cabins: "admin.cabins",
    experiences: "admin.experiences",
    inquiries: "admin.inquiries",
    reviews: "admin.reviews",
    newsletter: "admin.newsletter",
    guests: "admin.guests",
    accounting: "admin.accounting",
    gallery: "admin.gallery",
};

const EMPTY = { name: "", email: "", password: "", job_title: "", phone: "", permissions: [], active: true };

export default function EmployeesTab() {
    const { t } = useT();
    const [items, setItems] = useState([]);
    const [admins, setAdmins] = useState([]);
    const [sections, setSections] = useState([]);
    const [presets, setPresets] = useState({});
    const [editing, setEditing] = useState(null); // { ...employee } or EMPTY (new)
    const [audit, setAudit] = useState([]);

    const refresh = () => api.get("/admin/employees").then((r) => setItems(r.data));
    const refreshAudit = () => api.get("/admin/employees/audit").then((r) => setAudit(r.data)).catch(() => {});
    useEffect(() => {
        refresh();
        refreshAudit();
        api.get("/admin/users").then((r) => setAdmins((r.data || []).filter((u) => u.role === "admin"))).catch(() => {});
        api.get("/admin/employees/sections").then((r) => { setSections(r.data.sections); setPresets(r.data.presets); });
    }, []);

    const labelFor = (s) => t(SECTION_LABEL_KEY[s] || `admin.${s}`);

    const save = async () => {
        const e = editing;
        if (!e.name.trim() || !e.email.trim()) { toast.error(t("employees.nameEmailRequired")); return; }
        try {
            if (e.user_id) {
                const payload = { name: e.name, email: e.email, job_title: e.job_title, phone: e.phone, permissions: e.permissions, active: e.active };
                if (e.password) payload.password = e.password;
                await api.put(`/admin/employees/${e.user_id}`, payload);
            } else {
                if (!e.password || e.password.length < 6) { toast.error(t("employees.passwordMin")); return; }
                await api.post("/admin/employees", {
                    name: e.name, email: e.email, password: e.password,
                    job_title: e.job_title, phone: e.phone, permissions: e.permissions,
                });
            }
            toast.success(t("employees.saved"));
            setEditing(null);
            refresh();
            refreshAudit();
        } catch (err) {
            toast.error(err.response?.data?.detail || t("toast.saveFailed"));
        }
    };

    const remove = async (emp) => {
        if (!window.confirm(t("employees.deleteConfirm", emp.name))) return;
        await api.delete(`/admin/employees/${emp.user_id}`);
        toast.success(t("employees.removed"));
        refresh();
        refreshAudit();
    };

    const togglePerm = (s) => {
        const has = editing.permissions.includes(s);
        setEditing({ ...editing, permissions: has ? editing.permissions.filter((p) => p !== s) : [...editing.permissions, s] });
    };

    return (
        <div data-testid="admin-employees">
            <PageHeader
                eyebrow="Admin · Team"
                title={t("employees.title")}
                subtitle={t("employees.subtitle")}
                action={<button onClick={() => setEditing({ ...EMPTY })} data-testid="employee-new" className="inline-flex items-center gap-2 px-5 py-3 bg-forest text-bone text-xs uppercase tracking-widest2 font-semibold"><Plus size={14} /> {t("employees.add")}</button>}
            />

            {/* Owners & admins (read-only) */}
            <div className="mb-10" data-testid="admin-owners">
                <div className="eyebrow mb-3">{t("employees.ownersTitle")}</div>
                <div className="border border-espresso/15">
                    <table className="w-full text-sm">
                        <thead className="text-left text-xs uppercase tracking-widest2 text-espresso bg-bone2/50">
                            <tr>
                                <th className="px-4 py-3">{t("employees.cols.name")}</th>
                                <th className="px-4 py-3">{t("employees.cols.email")}</th>
                                <th className="px-4 py-3">{t("employees.cols.access")}</th>
                                <th className="px-4 py-3">{t("employees.cols.status")}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {admins.length === 0 && <tr><td colSpan={4} className="px-4 py-8 text-center text-muted2">{t("employees.ownersEmpty")}</td></tr>}
                            {admins.map((a) => (
                                <tr key={a.user_id} className="border-t border-espresso/10" data-testid={`owner-row-${a.user_id}`}>
                                    <td className="px-4 py-4 font-medium">{a.name}</td>
                                    <td className="px-4 py-4 text-muted2">{a.email}</td>
                                    <td className="px-4 py-4">
                                        <span className="px-2 py-0.5 text-[10px] uppercase tracking-widest2 bg-gold/20 text-espresso">{t("employees.ownerBadge")}</span>
                                    </td>
                                    <td className="px-4 py-4">
                                        <span className="px-2 py-1 text-[10px] uppercase tracking-widest2 bg-forest/10 text-forest">{t("employees.active")}</span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <p className="text-[11px] text-muted2 mt-2">{t("employees.ownersNote")}</p>
            </div>

            <div className="border border-espresso/15">
                <table className="w-full text-sm">
                    <thead className="text-left text-xs uppercase tracking-widest2 text-espresso bg-bone2/50">
                        <tr>
                            <th className="px-4 py-3">{t("employees.cols.name")}</th>
                            <th className="px-4 py-3">{t("employees.cols.email")}</th>
                            <th className="px-4 py-3">{t("employees.cols.access")}</th>
                            <th className="px-4 py-3">{t("employees.cols.status")}</th>
                            <th className="px-4 py-3" />
                        </tr>
                    </thead>
                    <tbody>
                        {items.length === 0 && <tr><td colSpan={5} className="px-4 py-12 text-center text-muted2">{t("employees.empty")}</td></tr>}
                        {items.map((emp) => (
                            <tr key={emp.user_id} className="border-t border-espresso/10" data-testid={`employee-row-${emp.user_id}`}>
                                <td className="px-4 py-4">
                                    <div className="font-medium">{emp.name}</div>
                                    {emp.job_title && <div className="text-xs text-muted2">{emp.job_title}</div>}
                                </td>
                                <td className="px-4 py-4 text-muted2">{emp.email}</td>
                                <td className="px-4 py-4">
                                    <div className="flex flex-wrap gap-1 max-w-md">
                                        {emp.permissions.length === 0 && <span className="text-xs text-muted2 italic">{t("employees.noAccess")}</span>}
                                        {emp.permissions.map((p) => (
                                            <span key={p} className="px-2 py-0.5 text-[10px] uppercase tracking-widest2 bg-forest/10 text-forest">{labelFor(p)}</span>
                                        ))}
                                    </div>
                                </td>
                                <td className="px-4 py-4">
                                    <span className={`px-2 py-1 text-[10px] uppercase tracking-widest2 ${emp.active ? "bg-forest/10 text-forest" : "bg-terracotta/15 text-terracotta"}`}>{emp.active ? t("employees.active") : t("employees.disabled")}</span>
                                </td>
                                <td className="px-4 py-4 text-right whitespace-nowrap">
                                    <button onClick={() => setEditing({ ...emp, password: "" })} data-testid={`employee-edit-${emp.user_id}`} className="text-xs uppercase tracking-widest2 text-forest hover:text-terracotta mr-4">{t("employees.edit")}</button>
                                    <button onClick={() => remove(emp)} data-testid={`employee-delete-${emp.user_id}`} className="text-xs uppercase tracking-widest2 text-terracotta hover:text-forest">{t("employees.delete")}</button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Activity log */}
            <div className="mt-12" data-testid="employee-audit-log">
                <div className="eyebrow mb-4">{t("employees.activityTitle")}</div>
                {audit.length === 0 ? (
                    <p className="text-sm text-muted2 italic" data-testid="employee-audit-empty">{t("employees.activityEmpty")}</p>
                ) : (
                    <ul className="border border-espresso/15 divide-y divide-espresso/10">
                        {audit.map((a) => (
                            <li key={a.id} className="px-4 py-3 flex items-start gap-3" data-testid={`audit-entry-${a.id}`}>
                                <span className={`mt-1 w-2 h-2 rounded-full shrink-0 ${a.action === "employee.deleted" ? "bg-terracotta" : a.action === "employee.created" ? "bg-forest" : "bg-gold"}`} />
                                <div className="min-w-0">
                                    <div className="text-sm">{a.summary}</div>
                                    <div className="text-[11px] text-muted2 mt-0.5">
                                        {a.actor_name || a.actor_email || "Admin"} · {new Date(a.created_at).toLocaleString()}
                                    </div>
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            {editing && (
                <Dialog open onOpenChange={() => setEditing(null)}>
                    <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="employee-dialog">
                        <DialogHeader>
                            <DialogTitle className="font-serif text-3xl">{editing.user_id ? t("employees.editTitle") : t("employees.addTitle")}</DialogTitle>
                            <DialogDescription>{t("employees.dialogDesc")}</DialogDescription>
                        </DialogHeader>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                            <AdminField label={t("employees.cols.name")} value={editing.name} onChange={(v) => setEditing({ ...editing, name: v })} testid="employee-form-name" />
                            <div>
                                <label className="eyebrow block mb-2">{t("employees.cols.email")}</label>
                                <input
                                    type="email" value={editing.email}
                                    onChange={(e) => setEditing({ ...editing, email: e.target.value })}
                                    data-testid="employee-form-email"
                                    className="w-full border border-espresso/20 px-3 py-2 text-sm bg-transparent focus:border-forest outline-none disabled:opacity-50"
                                />
                            </div>
                            <AdminField label={t("employees.jobTitle")} value={editing.job_title} onChange={(v) => setEditing({ ...editing, job_title: v })} testid="employee-form-job" />
                            <AdminField label={t("employees.phone")} value={editing.phone} onChange={(v) => setEditing({ ...editing, phone: v })} testid="employee-form-phone" />
                            <div className="md:col-span-2">
                                <label className="eyebrow block mb-2">{editing.user_id ? t("employees.resetPassword") : t("employees.password")}</label>
                                <input
                                    type="password" value={editing.password}
                                    onChange={(e) => setEditing({ ...editing, password: e.target.value })}
                                    placeholder={editing.user_id ? t("employees.leaveBlank") : ""}
                                    data-testid="employee-form-password"
                                    className="w-full border border-espresso/20 px-3 py-2 text-sm bg-transparent focus:border-forest outline-none"
                                />
                            </div>
                        </div>

                        {/* Role presets */}
                        <div className="mt-5">
                            <div className="eyebrow mb-2">{t("employees.applyPreset")}</div>
                            <div className="flex flex-wrap gap-2">
                                {Object.keys(presets).map((p) => (
                                    <button
                                        key={p}
                                        type="button"
                                        onClick={() => setEditing({ ...editing, permissions: [...presets[p]] })}
                                        data-testid={`employee-preset-${p}`}
                                        className="px-3 py-1.5 border border-espresso/30 text-xs uppercase tracking-widest2 hover:bg-bone2"
                                    >
                                        {t(`employees.presets.${p}`)}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Permission checkboxes */}
                        <div className="mt-5">
                            <div className="eyebrow mb-3">{t("employees.accessAreas")}</div>
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="employee-permissions">
                                {sections.map((s) => {
                                    const on = editing.permissions.includes(s);
                                    return (
                                        <button
                                            key={s}
                                            type="button"
                                            onClick={() => togglePerm(s)}
                                            data-testid={`employee-perm-${s}`}
                                            className={`flex items-center gap-2 border px-3 py-2 text-sm transition-all ${on ? "border-forest bg-forest/5" : "border-espresso/15 hover:border-espresso/40"}`}
                                        >
                                            <span className={`w-4 h-4 border flex items-center justify-center ${on ? "bg-forest border-forest" : "border-espresso/40"}`}>
                                                {on && <Check size={11} className="text-bone" />}
                                            </span>
                                            {labelFor(s)}
                                        </button>
                                    );
                                })}
                            </div>
                            <p className="text-[11px] text-muted2 mt-2">{t("employees.accessNote")}</p>
                        </div>

                        {editing.user_id && (
                            <label className="flex items-center gap-3 text-sm mt-4">
                                <input type="checkbox" checked={!!editing.active} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} data-testid="employee-form-active" />
                                {t("employees.activeAccount")}
                            </label>
                        )}

                        <DialogFooter className="mt-6">
                            <button onClick={() => setEditing(null)} className="px-5 py-3 border border-espresso/30 text-xs uppercase tracking-widest2">{t("common.cancel")}</button>
                            <button onClick={save} data-testid="employee-form-save" className="px-5 py-3 bg-forest text-bone text-xs uppercase tracking-widest2 font-semibold">{t("employees.save")}</button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            )}
        </div>
    );
}
